/* eslint-disable react/destructuring-assignment */
import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import TextareaAutosize from 'react-textarea-autosize';
import { Flexbox } from '@/components/flex';
import { INSPECTOR_BACK_ID } from '@/features/editor/inspector';
import { useIsPreviewingDesktop } from '@/features/editor/shared/use-device-type';
import { InspectorWidgetLayout } from '../shared/inspector-widget-layout';
import { EditorContext } from '@/features/editor/context/editor-context';
import { useWidgetControls } from '@/features/editor/widgets/shared/use-widget-controls';
import { GeneralText } from '@/features/editor/widgets/shared/general-text';
import { VSpace } from '@/components/spacing';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { ConfirmModal } from '@/components/confirm-modal';
import { TextInput } from '@/components/text-input';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { TabSelect } from '@/components/tab-select';
import { TrackingRules } from '@/features/editor/widgets/track-element/rules';
import {
  newTrackingElementContext,
  TrackElementContext,
} from '@/features/editor/widgets/track-element/context';
import { ElementTracking } from '@/webapi/use-experience-api';
import { CodeBlock } from '@/features/editor/widgets/track-element/code';
import { DeviceType } from '@/utils/definitions';

export const TrackElement: React.FC<ElementTracking> = (props) => {
  const isDesk = useIsPreviewingDesktop();
  const { inspectorNav } = useContext(EditorContext);

  const sourceUrlRef = useRef(null);
  const sharedElement = useModalSharedElement();
  const textRef = useRef<HTMLInputElement>(null);

  const ctx = newTrackingElementContext(props);
  const { WidgetControls, controlsProps } = useWidgetControls(() => {
    if (isValid(ctx, textRef)) {
      ctx.onSave();
      inspectorNav.gotoChangelog();
    }
  }, `Add Event`);
  useRemoveElementPlaceholder();
  const onBackClicked = async () => {
    if (ctx.hasChanges()) sharedElement.show();
    else inspectorNav.gotoChangelog();
  };
  return (
    <TrackElementContext.Provider value={ctx}>
      <Wrapper isDesk={isDesk} ref={sourceUrlRef}>
        <SharedElementOverlay
          {...sharedElement.props}
          fromElement={sourceUrlRef}
        >
          <ConfirmModal
            neutral
            onDiscard={sharedElement.hide}
            onConfirm={() => inspectorNav.gotoChangelog()}
            title="You have unsaved changes"
            description="Are you sure you want to discard the changes? "
            yesCaption="Discard Changes"
            noCaption="Continue Editing"
          />
        </SharedElementOverlay>
        <InspectorWidgetLayout
          progress={75}
          title="Define New Event"
          backCaption="< back to the experience"
          onBackClicked={onBackClicked}
          footer={
            <WidgetControls
              {...{
                ...controlsProps,
                onSave: onBackClicked,
              }}
              saveText="Discard"
              alwaysEnabled
              showHistory={false}
              hidePreview
            />
          }
        >
          <GeneralText>
            Set up a new event to track extra clicks and CTR on a specific
            element in your test
          </GeneralText>
          <VSpace value={2} />
          <LabelText>Event Name</LabelText>
          <VSpace value={1} />
          <NameInput
            ref={textRef}
            maxLength={20}
            placeholder="Unnamed event"
            defaultValue={props.name}
            onChange={(e) => {
              ctx.setEventName(e.target.value);
              if (textRef.current.style.borderColor === `red`) {
                textRef.current.style.borderColor = `#c7c8cf`;
              }
            }}
          />
          <VSpace value={2} />
          <LabelText>Add event description (optional)</LabelText>
          <VSpace value={1} />
          <TextArea
            defaultValue={ctx?.description || ``}
            onChange={(e) => ctx.setDescription(e.target.value)}
          />
          <VSpace value={3} />
          <TabSelect
            onTabSelected={(tab) => ctx.setType(tab ? `code` : `rule`)}
            tabHeight={4}
            tabFont={`'Inter', sans-serif;`}
            tabFontSize="1.2rem"
            tabWidth={23}
            tabs={[`Rule based`, `Custom`]}
            defaultValue={ctx.type === `rule` ? `Rule based` : `Custom`}
          />
          {ctx.type === `rule` ? <TrackingRules /> : <CodeBlock />}
        </InspectorWidgetLayout>
      </Wrapper>
    </TrackElementContext.Provider>
  );
};

const LabelText = styled.span`
  color: #b9c0c7;
  font-size: 1.4rem;
  font-family: Inter, serif;
  font-weight: 500;
`;

const TextArea = styled<{ device: DeviceType }>(TextareaAutosize)`
  && {
    appearance: none;
    -webkit-appearance: none;
    pointer-events: auto;
    outline: none;
    border: 1px solid rgb(221, 219, 219);
    border-radius: 10px;
    text-align: left;
    font-family: Inter, serif;
    color: #5b656e;
    overflow-y: scroll;

    &&::-webkit-outer-spin-button,
    &&::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &&[type='number'] {
      -moz-appearance: textfield;
    }

    resize: none;
    letter-spacing: -0.44px;

    margin: 0;
    width: 100%;
    min-height: 2rem;
    padding: 1.2rem;
    font-weight: 500;
  }
`;

const NameInput = styled(TextInput)`
  && {
    border: 1px solid rgb(221, 219, 219);
    box-shadow: none;
    margin: 0;
    width: 100%;
    padding: 1.2rem;
    font-size: 1.4rem;
    font-weight: 500;
  }
`;

const Wrapper = styled(Flexbox)`
  && {
    height: 100%;
    width: ${(p: any) => (p.isDesk ? `100%` : `65%`)};

    #${INSPECTOR_BACK_ID} {
      z-index: 31;
    }
  }
`;

const useModalSharedElement = () =>
  useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#dedede`,
        opacity: `0`,
      },
      extraTo: {
        background: `white`,
        opacity: `1`,
      },
    },
    undefined,
    () => centered(18, 42),
  );

const useRemoveElementPlaceholder = () => {
  useEffect(() => {
    setTimeout(() => {
      const iframe = document.querySelector(
        `#navigator-iframe`,
      ) as HTMLIFrameElement;
      if (iframe?.contentDocument) {
        iframe.contentDocument
          .querySelectorAll(`.__loomi_element_placeholder`)
          ?.forEach((x) => x.remove());
      }
    }, 500);
  }, []);
};

const isValid = (
  ctx: TrackElementContext,
  textRef: React.MutableRefObject<HTMLInputElement>,
) => {
  if (ctx.jsCodeInvalid) {
    toast(`Js Code fails to compile.`, {
      theme: `colored`,
      type: `warning`,
    });
    return false;
  }
  if (!ctx.name) {
    toast(`Please Set Event Name`, {
      theme: `colored`,
      type: `warning`,
    });
    textRef.current.focus();
    textRef.current.style.borderColor = `red`;
    return false;
  }
  const emptyXIdx = ctx?.qbContext?.queryState?.findIndex(
    (x) => !!x.values?.find((x) => !x.value),
  );
  if (emptyXIdx >= 0 && ctx.type === `rule`) {
    toast(`Element selector cant be empty`, {
      theme: `colored`,
      type: `warning`,
    });

    const emptyYIdx = ctx?.qbContext?.queryState?.[
      emptyXIdx
    ]?.values?.findIndex((v) => !v.value);
    ctx.setFocusedInput(emptyXIdx, emptyYIdx);
    return false;
  }
  return true;
};
